import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { ErrorResponse } from '@core/error/error-response.model';
import { HistoryItem } from '@api/models/ClientSupportApi/Contract/history-item';
import { inject, Injectable } from '@angular/core';
import { CustomRxState } from '@core/state/rx.state';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { SupportService } from '@api/services/support.service';
import { startWithLoading } from '@core/state/operators/start-with-loading';
import { Observable } from 'rxjs';
import { AddSupportNoteRequest } from '@api/models/ClientSupportApi/Contract/Notes/add-support-note-request';
import { map, tap } from 'rxjs/operators';
import { TicketInfo } from '@api/models/ClientSupportApi/Contract/ticket-info';
import { ListOpNoteResponse } from '@api/models/ClientSupportApi/Contract/Notes/list-op-note-response';
import { SupportNoteTransactionBody } from '../model/support-note.model';
import { AddOpNoteRequest } from '@api/models/ClientSupportApi/Contract/Notes/add-op-note-request';
import { HistoryFilterBody } from '../model/history-filter';


export interface IHistoryState {
    loading: Record<StartWithUnit, boolean>;
    errors: Record<StartWithUnit, ErrorResponse>;
    clientHistory: HistoryItem[];
    clientHistoryFilter: HistoryFilterBody;
}

export interface IHistoryAction {
    requestClientHistory: HistoryItem[];
    setClientHistoryFilter: HistoryFilterBody;
}

@Injectable({
    providedIn: 'root',
})
export class HistoryState extends CustomRxState<IHistoryState> {
    readonly #actions: RxActions<IHistoryAction> = new RxActionFactory<IHistoryAction>().create();
    readonly #supportService: SupportService = inject(SupportService);

    public readonly clientHistory$: Observable<HistoryItem[]> = this.select('clientHistory').pipe(
        /** TODO Check this manipulations (legacy)*/
        map((historyItems: HistoryItem[]) =>
            historyItems?.map((item: HistoryItem) => ({
                ...item,
                localDateNumberValue: new Date(item.localDate).getTime(),
                procTimeNumberValue: new Date(item.procTime).getTime(),
            })),
        ));

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');

    constructor() {
        super();

        this.connectSelectors();
        this.setDefaultState();
    }


    public requestClientHistory(id: number, body: HistoryFilterBody, mediumId?: number): void {
        this.#actions.setClientHistoryFilter(body);

        this.hold(
            this.#supportService.apiSupportClientsIdHistoryGet({ id, ...body, mediumId }).pipe(
                startWithLoading(this, StartWithUnit.LoadClientHistory),
            ),
            this.#actions.requestClientHistory,
        );
    }

    public addHistoryNote(body: AddSupportNoteRequest): Observable<void> {
        return this.#supportService.apiSupportAddSupportNotePost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddHistoryNoteItem),
            tap(() => this.requestClientHistory(body.client, this.get('clientHistoryFilter'))),
        );
    }

    public getHistoryTicketInfo(trnId: number, id: number): Observable<TicketInfo> {
        return this.#supportService.apiSupportClientsIdTicketTrnIdGet({ trnId, id });
    }

    public getOperationNoteList(body: SupportNoteTransactionBody): Observable<ListOpNoteResponse[]> {
        return this.#supportService.apiSupportListOpNoteGet(body);
    }

    public addNoteForOperation(body: AddOpNoteRequest): Observable<void> {
        return this.#supportService.apiSupportAddOpNotePost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddNoteForOperation),
        );
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            errors: null,
            clientHistory: null,
            clientHistoryFilter: null,
        });
    }

    private connectSelectors(): void {
        this.connect('clientHistory', this.#actions.requestClientHistory$);
        this.connect('clientHistoryFilter', this.#actions.setClientHistoryFilter$);
    }
}
